/**
 * Analyst Bench Service
 *
 * Provides data fetching functions for the Analyst Bench with period mode support.
 * Uses the backend APIs with ?mode= parameter for annual/quarterly/monthly views.
 */

import { get } from './api';
import type { PeriodMode } from '../types/periodMode';

// ============================================
// Types
// ============================================

export interface BalanceSheetItem {
  balance_sheet_item_code: string;
  balance_sheet_item_name: string;
  balance_sheet_item_value: number;
  period: string;
}

export interface BalanceSheetResponse {
  success: boolean;
  institution_name: string;
  periods: string[];
  data: BalanceSheetItem[];
}

export interface IndicatorValue {
  indicator_id: string;
  indicator_name: string;
  indicator_category: string;
  values: Record<string, number | null>;
}

export interface IndicatorsResponse {
  success: boolean;
  institution_name: string;
  periods: string[];
  data: IndicatorValue[];
}

// ============================================
// Data Transformation Types
// ============================================

export interface FinancialsRow {
  code: string;
  name: string;
  indent: number;
  isCalculated?: boolean;
  values: Record<string, number>;
}

export interface FinancialsSection {
  id: string;
  name: string;
  rows: FinancialsRow[];
}

export interface FinancialsData {
  institutionName: string;
  periods: string[];
  sections: {
    activos: FinancialsRow[];
    pasivos: FinancialsRow[];
    patrimonio: FinancialsRow[];
    resultados: FinancialsRow[];
  };
}

export interface IndicatorRow {
  id: string;
  name: string;
  category: string;
  values: Record<string, number | null>;
}

export interface IndicatorsData {
  institutionName: string;
  periods: string[];
  categories: {
    name: string;
    indicators: IndicatorRow[];
  }[];
}

// ============================================
// Account Configuration
// ============================================

// Account codes and their display names for balance sheet
const ACCOUNT_CONFIG: Record<string, { name: string; indent: number; section: string }> = {
  '1': { name: 'ACTIVO', indent: 0, section: 'activos' },
  '11': { name: 'Fondos Disponibles', indent: 1, section: 'activos' },
  '13': { name: 'Inversiones', indent: 1, section: 'activos' },
  '14': { name: 'Cartera de Créditos', indent: 1, section: 'activos' },
  '16': { name: 'Cuentas por Cobrar', indent: 1, section: 'activos' },
  '17': { name: 'Bienes Realizables', indent: 1, section: 'activos' },
  '18': { name: 'Propiedades y Equipos', indent: 1, section: 'activos' },
  '19': { name: 'Otros Activos', indent: 1, section: 'activos' },
  '2': { name: 'PASIVO', indent: 0, section: 'pasivos' },
  '21': { name: 'Obligaciones con el Público', indent: 1, section: 'pasivos' },
  '25': { name: 'Cuentas por Pagar', indent: 1, section: 'pasivos' },
  '26': { name: 'Obligaciones Financieras', indent: 1, section: 'pasivos' },
  '29': { name: 'Otros Pasivos', indent: 1, section: 'pasivos' },
  '3': { name: 'PATRIMONIO', indent: 0, section: 'patrimonio' },
  '31': { name: 'Capital Social', indent: 1, section: 'patrimonio' },
  '33': { name: 'Reservas', indent: 1, section: 'patrimonio' },
  '34': { name: 'Otros Aportes Patrimoniales', indent: 1, section: 'patrimonio' },
  '35': { name: 'Superávit por Valuaciones', indent: 1, section: 'patrimonio' },
  '36': { name: 'Resultados', indent: 1, section: 'patrimonio' },
};

// Income statement accounts
const RESULTADOS_ACCOUNTS = [
  { code: '51', name: 'Intereses y Descuentos Ganados', indent: 0 },
  { code: '41', name: 'Intereses Causados', indent: 0 },
  { code: '52', name: 'Comisiones Ganadas', indent: 0 },
  { code: '42', name: 'Comisiones Causadas', indent: 0 },
  { code: '53', name: 'Utilidades Financieras', indent: 0 },
  { code: '43', name: 'Pérdidas Financieras', indent: 0 },
  { code: '54', name: 'Ingresos por Servicios', indent: 0 },
  { code: '44', name: 'Provisiones', indent: 0 },
  { code: '45', name: 'Gastos de Operación', indent: 0 },
  { code: '55', name: 'Otros Ingresos Operacionales', indent: 0 },
  { code: '46', name: 'Otras Pérdidas Operacionales', indent: 0 },
  { code: '56', name: 'Otros Ingresos', indent: 0 },
  { code: '47', name: 'Otros Gastos y Pérdidas', indent: 0 },
  { code: '48', name: 'Impuestos y Participación', indent: 0 },
];

// Indicator category order
const CATEGORY_ORDER = [
  'Solvencia',
  'Calidad de Activos',
  'Eficiencia y Gestion',
  'Rentabilidad',
  'Liquidez',
  'Sensibilidad Tasa Interes',
];

// Indicators that should be displayed as ratios instead of percentages
const RATIO_INDICATORS = ['S2', 'S4'];

// ============================================
// API Functions
// ============================================

/**
 * Fetch balance sheet data with period mode
 */
export async function fetchFinancialsWithMode(
  institutionId: string,
  mode: PeriodMode
): Promise<FinancialsData> {
  const response = await get<BalanceSheetResponse>(
    `/api/balance-sheet/${institutionId}?mode=${mode}`
  );

  if (!response.success) {
    throw new Error('Failed to fetch balance sheet data');
  }

  return transformBalanceSheetData(response);
}

/**
 * Fetch financial indicators with period mode
 */
export async function fetchIndicatorsWithMode(
  institutionId: string,
  mode: PeriodMode
): Promise<IndicatorsData> {
  const response = await get<IndicatorsResponse>(
    `/api/financial-indicators/${institutionId}?mode=${mode}`
  );

  if (!response.success) {
    throw new Error('Failed to fetch indicators data');
  }

  return transformIndicatorsData(response);
}

// ============================================
// CSV Export Functions
// ============================================

function downloadCSV(rows: string[][], filename: string): void {
  const csv = rows
    .map((row) =>
      row
        .map((cell) => {
          const str = String(cell ?? '');
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportFinancialsMonthly(
  institutionId: string,
  institutionName: string
): Promise<void> {
  const data = await fetchFinancialsWithMode(institutionId, 'monthly');

  const sectionLabels: Record<keyof typeof data.sections, string> = {
    activos: 'Activos',
    pasivos: 'Pasivos',
    patrimonio: 'Patrimonio',
    resultados: 'Resultados',
  };

  const headers = ['Section', 'Code', 'Name', ...data.periods.map(formatPeriodHeader)];
  const rows: string[][] = [headers];

  for (const [key, label] of Object.entries(sectionLabels)) {
    const sectionRows = data.sections[key as keyof typeof data.sections];
    for (const row of sectionRows) {
      rows.push([
        label,
        row.code,
        row.name,
        ...data.periods.map((p) => formatFinancialValue(row.values[p], 2)),
      ]);
    }
  }

  const safeName = institutionName.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 40);
  downloadCSV(rows, `${safeName}_financials_monthly.csv`);
}

export async function exportIndicatorsMonthly(
  institutionId: string,
  institutionName: string
): Promise<void> {
  const data = await fetchIndicatorsWithMode(institutionId, 'monthly');

  const headers = ['Category', 'ID', 'Name', ...data.periods.map(formatPeriodHeader)];
  const rows: string[][] = [headers];

  for (const category of data.categories) {
    for (const indicator of category.indicators) {
      rows.push([
        category.name,
        indicator.id,
        indicator.name,
        ...data.periods.map((p) => formatIndicatorValue(indicator.values[p], indicator.id)),
      ]);
    }
  }

  const safeName = institutionName.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 40);
  downloadCSV(rows, `${safeName}_key_ratios_monthly.csv`);
}

// ============================================
// Data Transformation Functions
// ============================================

/**
 * Transform raw balance sheet response into structured data
 */
function transformBalanceSheetData(response: BalanceSheetResponse): FinancialsData {
  const { institution_name, periods, data } = response;

  // Create a map of code -> period -> value
  const valueMap: Record<string, Record<string, number>> = {};
  for (const item of data) {
    if (!valueMap[item.balance_sheet_item_code]) {
      valueMap[item.balance_sheet_item_code] = {};
    }
    valueMap[item.balance_sheet_item_code][item.period] = item.balance_sheet_item_value;
  }

  // Build sections
  const sections: FinancialsData['sections'] = {
    activos: [],
    pasivos: [],
    patrimonio: [],
    resultados: [],
  };

  // Build main balance sheet sections
  for (const [code, config] of Object.entries(ACCOUNT_CONFIG)) {
    const row: FinancialsRow = {
      code,
      name: config.name,
      indent: config.indent,
      values: {},
    };

    for (const period of periods) {
      row.values[period] = valueMap[code]?.[period] || 0;
    }

    const section = config.section as keyof typeof sections;
    if (section !== 'resultados') {
      sections[section].push(row);
    }
  }

  // Build resultados section
  for (const account of RESULTADOS_ACCOUNTS) {
    const row: FinancialsRow = {
      code: account.code,
      name: account.name,
      indent: account.indent,
      values: {},
    };

    for (const period of periods) {
      row.values[period] = valueMap[account.code]?.[period] || 0;
    }

    sections.resultados.push(row);
  }

  return {
    institutionName: institution_name,
    periods,
    sections,
  };
}

/**
 * Transform raw indicators response into structured data
 */
function transformIndicatorsData(response: IndicatorsResponse): IndicatorsData {
  const { institution_name, periods, data } = response;

  // Group indicators by category
  const categoryMap: Record<string, IndicatorRow[]> = {};

  for (const indicator of data) {
    if (!categoryMap[indicator.indicator_category]) {
      categoryMap[indicator.indicator_category] = [];
    }

    categoryMap[indicator.indicator_category].push({
      id: indicator.indicator_id,
      name: indicator.indicator_name,
      category: indicator.indicator_category,
      values: indicator.values,
    });
  }

  // Build categories in order
  const categories = CATEGORY_ORDER
    .filter((cat) => categoryMap[cat] && categoryMap[cat].length > 0)
    .map((name) => ({
      name,
      indicators: categoryMap[name] || [],
    }));

  return {
    institutionName: institution_name,
    periods,
    categories,
  };
}

// ============================================
// Formatting Functions
// ============================================

/**
 * Format a numeric value for display in the table
 */
export function formatFinancialValue(value: number | undefined | null, decimals = 0): string {
  if (value === undefined || value === null || isNaN(value) || value === 0) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format indicator value based on indicator type
 */
export function formatIndicatorValue(
  value: number | null | undefined,
  indicatorId: string
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '-';
  }

  // S2 and S4 are displayed as ratios (plain numbers)
  if (RATIO_INDICATORS.includes(indicatorId)) {
    return value.toFixed(2);
  }

  // All others displayed as percentages
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Format period string for column header display
 */
export function formatPeriodHeader(period: string): string {
  const [year, month] = period.split('-');
  const monthNames = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
  ];
  const monthIndex = parseInt(month, 10) - 1;
  const yearShort = year.slice(2);
  return `${monthNames[monthIndex]}-${yearShort}`;
}
